import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Sparkles, Quote } from "lucide-react";
import { JEREMY, PHOTOS } from "@/lib/jeremy";
import PageMeta from "@/components/site/PageMeta";
import ContactJeremy from "@/components/site/ContactJeremy";
import ServiceArea from "@/components/site/ServiceArea";

export default function MeetJeremyPage() {
  return (
    <main className="pt-24 md:pt-32" data-testid="meet-jeremy-page">
      <PageMeta
        title="Meet Jeremy | Navy Veteran & ION Solar Representative"
        description="Jeremy is a Navy veteran born and raised in Portsmouth, Virginia and an authorized ION Solar representative. He helps Virginia homeowners understand their electric bills and explore whether solar makes sense for their home."
      />
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow">Meet Jeremy</div>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0F382C] max-w-4xl">
            Local. Honest. Not going anywhere.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">
            {JEREMY.mission}
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#FEF3C7] rounded-[2rem] rotate-2" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-[#E5E0D8] shadow-xl">
                <img src={PHOTOS.meet} alt="Jeremy" className="w-full h-[520px] object-cover" data-testid="meet-jeremy-main-photo" />
              </div>
              <div className="absolute -bottom-4 -right-4 rounded-2xl bg-[#0F382C] text-[#FEF3C7] px-4 py-3">
                <div className="flex items-center gap-2 text-xs font-mono-metric uppercase tracking-widest">
                  <MapPin className="w-3.5 h-3.5" /> {JEREMY.city}
                </div>
              </div>
            </div>

            <div className="mt-16 rounded-3xl overflow-hidden border border-[#E5E0D8]">
              <img src={PHOTOS.casual} alt="Jeremy — casual" className="w-full h-64 object-cover object-top" />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-8">
            <div>
              <div className="eyebrow">Jeremy's Story</div>
              <h2 className="mt-3 font-serif-display text-3xl text-[#0F382C]">Why I help Virginia homeowners</h2>
              <div className="mt-5 space-y-4 text-slate-600 leading-relaxed text-base md:text-lg">
                <p>I'm Jeremy, a Navy veteran born and raised in Portsmouth, Virginia. For the past five years, I've worked with ION Solar to help Virginia homeowners understand their electric bills and explore whether solar makes sense for their homes.</p>
                <p>My approach is simple: explain the numbers, answer your questions, and make the process easier to understand. You deserve to understand what you're considering before making a decision.</p>
                <p>From the first conversation through qualification, approvals, installation, and activation, I stay involved as your local point of contact. ION Solar handles the professional installation and service, and I help you understand each step along the way.</p>
              </div>
            </div>

            <div>
              <div className="eyebrow">My Approach</div>
              <h3 className="mt-3 font-serif-display text-2xl text-[#0F382C]">Educate first. Sell second. Follow through always.</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
                {JEREMY.facts.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 text-sm text-[#0F382C]">
                    <Sparkles className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-warm p-6">
              <Quote className="w-5 h-5 text-[#D97706] mb-2" />
              <p className="font-serif-display text-lg text-[#0F382C] leading-snug">
                "If solar doesn't make sense for your home, I'll tell you. That honesty is the whole reason people send me their bills."
              </p>
              <div className="mt-3 text-xs font-mono-metric uppercase tracking-widest text-slate-500">— Jeremy</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/qualify" className="btn-solar text-sm" data-testid="meet-jeremy-page-qualify">
                See If My Home Qualifies <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/qualify?upload=1" className="btn-ghost-forest text-sm" data-testid="meet-jeremy-page-review-bill">
                Have Jeremy Review My Bill <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServiceArea />
      <ContactJeremy />
    </main>
  );
}
